import React, { useRef } from 'react';
import { Upload, RefreshCw, Settings2, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { AccountData, parseNum } from '../../lib/campaignIntelligence';

interface AccountBlockProps {
  account: AccountData;
  canRemove: boolean;
  onRename: (id: number, name: string) => void;
  onRemove: (id: number) => void;
  onFileLoad: (acctId: number, type: 'meta' | 'rt', file: File) => void;
  onRemap: (acctId: number, type: 'meta' | 'rt') => void;
}

export function AccountBlock({ account, canRemove, onRename, onRemove, onFileLoad, onRemap }: AccountBlockProps) {
  const metaRef = useRef<HTMLInputElement>(null);
  const rtRef = useRef<HTMLInputElement>(null);

  const metaReady = !!account.metaRows;
  const rtReady = !!account.rtRows;
  const bothReady = metaReady && rtReady;

  const handleFile = (type: 'meta' | 'rt') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileLoad(account.id, type, file);
      e.target.value = '';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Account</span>
        <input
          type="text"
          value={account.name}
          onChange={(e) => onRename(account.id, e.target.value)}
          placeholder="Account name"
          className="bg-muted border border-border rounded-md px-2.5 py-1.5 text-sm font-semibold text-foreground outline-none focus:border-primary w-48"
        />
        {bothReady && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
            ✓ Ready
          </span>
        )}
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(account.id)} className="ml-auto text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30">
            <Trash2 size={14} className="mr-1" /> Remove
          </Button>
        )}
      </div>

      {/* File slots */}
      <div className="grid grid-cols-2 gap-3 p-4">
        {/* Meta slot */}
        <FileSlot
          label="Meta Ads Export"
          color="blue"
          icon="🔵"
          loaded={metaReady}
          fileName={account.metaFile}
          rowCount={account.metaRows?.length}
          spendCount={account.metaRows?.filter(r => parseNum(r.spend) > 0).length}
          hint="Needs: Campaign name, Amount spent, Unique link clicks, CPM, Cost per result, Results"
          inputRef={metaRef}
          onClickUpload={() => metaRef.current?.click()}
          onFileChange={handleFile('meta')}
          onRemap={metaReady ? () => onRemap(account.id, 'meta') : undefined}
        />

        {/* RT slot */}
        <FileSlot
          label="RedTrack Export"
          color="red"
          icon="🔴"
          loaded={rtReady}
          fileName={account.rtFile}
          rowCount={account.rtRows?.length}
          hint="Needs: Campaign name, Revenue, Conversions. Optional: LP Clicks, Offer Page EPC"
          inputRef={rtRef}
          onClickUpload={() => rtRef.current?.click()}
          onFileChange={handleFile('rt')}
          onRemap={rtReady ? () => onRemap(account.id, 'rt') : undefined}
        />
      </div>
    </div>
  );
}

interface FileSlotProps {
  label: string;
  color: 'blue' | 'red';
  icon: string;
  loaded: boolean;
  fileName: string | null;
  rowCount?: number;
  spendCount?: number;
  hint: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onClickUpload: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemap?: () => void;
}

function FileSlot({ label, color, icon, loaded, fileName, rowCount, spendCount, hint, inputRef, onClickUpload, onFileChange, onRemap }: FileSlotProps) {
  const borderColor = loaded
    ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-950/20'
    : 'border-dashed border-border hover:border-blue-400 dark:hover:border-blue-500 bg-muted/30 hover:bg-blue-50/50 dark:hover:bg-blue-950/20';

  return (
    <div
      className={`border-2 rounded-lg p-4 flex flex-col gap-2.5 transition-colors ${borderColor} ${!loaded ? 'cursor-pointer' : ''}`}
      onClick={!loaded ? onClickUpload : undefined}
    >
      <div className="text-xs font-bold flex items-center gap-2 text-foreground">
        <span>{icon}</span> {label}
      </div>

      {loaded ? (
        <>
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-green-100/80 dark:bg-green-900/30 rounded-md border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-400">
            <CheckCircle size={12} />
            <span className="font-medium truncate">{fileName}</span>
            <span className="text-muted-foreground">— {rowCount} rows{spendCount !== undefined && ` (${spendCount} with spend)`}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onClickUpload(); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground text-[11px] font-medium hover:bg-muted transition-colors"
            >
              <RefreshCw size={11} /> Replace file
            </button>
            {onRemap && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemap(); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground text-[11px] font-medium hover:bg-muted transition-colors"
              >
                <Settings2 size={11} /> Edit mapping
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="text-center py-4">
            <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
            <div className="text-xs font-semibold text-foreground mb-1">Click to upload</div>
            <div className="text-[10px] text-muted-foreground">.xlsx, .xls or .csv</div>
          </div>
          <div className="text-[10px] text-muted-foreground border-t border-border pt-2">{hint}</div>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onFileChange}
        className="hidden"
      />
    </div>
  );
}
